import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { ProductionDayBatch, TimelineBatch, ProcessParameter } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import { formatClockTime, processParamTimeToInput } from "../../../utils/processParameterSerialize";
import BatchMaterialConfirm from "../../../components/MES/BatchMaterialConfirm";
import PhaseMaterialConfirm from "./PhaseMaterialConfirm";
import {
  ProcessField,
  buildProcessForm,
  derivedDisplayValue,
  processFieldsForPhases,
  processPayloadFromForm,
} from "./processFields";
import "./TabletWorkOrders.scss";
import { dateLocale, t } from "../../../i18n/locale";

/** Kodovi proizvodnih faza (ProductionPhaseType.Code) koje pokriva pojedini tab. */
const TAB_PHASE_CODES: Record<Tab, string[]> = {
  mixer: ["mixing"],
  processor: ["bulk_fermentation", "final_proof"],
  oven: ["baking"],
  packaging: ["packaging"],
};

/**
 * Grupe procesnih polja koje tab prikazuje (processFields.ts). Šire od
 * TAB_PHASE_CODES: `quality` i `notes` nisu proizvodne faze nego mjesto
 * mjerenja, a hlađenje se bilježi na peći. Zahvaljujući tome operater na nalogu
 * vidi ISTA polja koja tehnolog vidi u "Procesnim podacima" — dosad je unos na
 * tabletu pokrivao samo dio njih.
 */
const TAB_PROCESS_GROUPS: Record<Tab, string[]> = {
  mixer: ["mixing", "notes"],
  processor: ["bulk_fermentation", "final_proof", "notes"],
  oven: ["baking", "cooling", "notes"],
  packaging: ["packaging", "quality", "notes"],
};

type Tab = "mixer" | "processor" | "oven" | "packaging";

const TAB_LABELS: Record<Tab, string> = {
  mixer: "Mikser",
  processor: "Obrada",
  oven: "Peć",
  packaging: "Pakiranje",
};

const fieldsForTab = (tab: Tab): ProcessField[] => processFieldsForPhases(TAB_PROCESS_GROUPS[tab]);

/**
 * Semi-finished products (starter, enzimi…) are consumed inside other batches
 * and never packaged as finished goods, so they are excluded from the packaging
 * station.
 */
function isSemiFinishedBatch(batch: TimelineBatch): boolean {
  return (
    batch.IsSemiFinished === true ||
    batch.is_semi_finished === true ||
    (batch.ProductID != null && batch.ProductID < 0)
  );
}

/**
 * Whether a batch actually goes through the obrada (processing) / peć (baking)
 * phase. Semi-finished products inherit the parent product's machine names for
 * scheduling, so we cannot rely on ProcessName/OvenName alone — a starter would
 * otherwise look like it needs baking and stay stuck "in progress". The batch's
 * own phase duration is the reliable signal.
 */
function hasProcessingPhase(batch: TimelineBatch): boolean {
  return (Number(batch.ProcessingTimeMin) || 0) > 0;
}

function hasBakingPhase(batch: TimelineBatch): boolean {
  return (Number(batch.BakingTimeMin) || 0) > 0;
}

const TIME_DEFAULT_FROM_BATCH: Record<string, keyof TimelineBatch> = {
  MixingStart: "MixingStart",
  MixingEnd: "MixingEnd",
  FinalProofStart: "ProcessingStart",
  FinalProofEnd: "ProcessingEnd",
  BakingStart: "BakingStart",
  BakingEnd: "BakingEnd",
};

function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

function toTimeInput(v?: string): string {
  if (!v) return "";
  const str = String(v).trim();
  const hm = str.match(/^(\d{1,2}):(\d{2})/);
  if (hm) return `${hm[1].padStart(2, "0")}:${hm[2]}`;
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function nowTime(): string {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function filterBatches(batches: TimelineBatch[], tab: Tab): TimelineBatch[] {
  const list =
    tab === "mixer"
      ? batches
      : tab === "processor"
      ? batches.filter((b) => hasProcessingPhase(b))
      : tab === "oven"
      ? batches.filter((b) => hasBakingPhase(b))
      : // packaging: only finished goods (semi-finished are not packaged)
        batches.filter((b) => !isSemiFinishedBatch(b));
  const key: keyof TimelineBatch =
    tab === "mixer"
      ? "MixingStart"
      : tab === "processor"
      ? "ProcessingStart"
      : tab === "oven"
      ? "BakingStart"
      : "BakingEnd";
  return [...list].sort((a, b) => {
    const ta = a[key] ? new Date(a[key] as string).getTime() : 0;
    const tb = b[key] ? new Date(b[key] as string).getTime() : 0;
    return ta - tb || (a.Sequence ?? 0) - (b.Sequence ?? 0);
  });
}

function fmtTime(d?: string) {
  return formatClockTime(d, "—");
}

const STATUS_LABELS: Record<string, string> = {
  planned: "Planirano",
  ready: "Spremno",
  in_progress: "U tijeku",
  completed: "Završeno",
  on_hold: "Pauza",
  skipped: "Preskočeno",
  canceled: "Otkazano",
};

type TabPhaseStatus = "planned" | "in_progress" | "completed";

const TAB_START_KEYS: Record<Tab, string> = {
  mixer: "MixingStart",
  processor: "FinalProofStart",
  oven: "BakingStart",
  packaging: "PackagingStart",
};

const TAB_END_KEYS: Record<Tab, string> = {
  mixer: "MixingEnd",
  processor: "FinalProofEnd",
  oven: "BakingEnd",
  packaging: "PackagingEnd",
};

/**
 * Match a batch to its process-parameter row by the unique TimelineBatch id.
 * MixCode is NOT unique on a production day (the same code can recur across
 * plans / semi-finished), so matching by MixCode can borrow another batch's
 * row — which leaked foreign phase data (e.g. a stray BakingStart) into this
 * card, showing a false "U tijeku" and a wrong prediction. Only fall back to
 * MixCode for legacy rows that predate the TimelineBatch link, and never pick
 * a row that belongs to a different batch.
 */
function findParam(
  params: ProcessParameter[],
  batch: TimelineBatch
): ProcessParameter | undefined {
  const byBatch = params.find((p) => p.TimelineBatch != null && p.TimelineBatch === batch.ID);
  if (byBatch) return byBatch;
  return params.find((p) => p.TimelineBatch == null && p.MixCode === batch.MixCode);
}

function paramRaw(
  params: ProcessParameter[],
  batch: TimelineBatch
): Record<string, unknown> | undefined {
  const p = findParam(params, batch);
  return p ? (p as unknown as Record<string, unknown>) : undefined;
}

function hasParamValue(raw: Record<string, unknown> | undefined, key: string): boolean {
  if (!raw) return false;
  const v = raw[key];
  return v != null && String(v).trim() !== "";
}

function batchNeedsPhase(batch: TimelineBatch, phase: Tab): boolean {
  if (phase === "mixer") return true;
  if (phase === "processor") return hasProcessingPhase(batch);
  if (phase === "oven") return hasBakingPhase(batch);
  // packaging: every finished good is packaged; semi-finished are not.
  return !isSemiFinishedBatch(batch);
}

/** Status po fazi (mikser / obrada / peć), ne globalni batch_status. */
function getTabPhaseStatus(
  phase: Tab,
  params: ProcessParameter[],
  batch: TimelineBatch
): TabPhaseStatus {
  const raw = paramRaw(params, batch);
  if (hasParamValue(raw, TAB_END_KEYS[phase])) return "completed";
  if (hasParamValue(raw, TAB_START_KEYS[phase])) return "in_progress";
  return "planned";
}

interface PhaseActualSummary {
  start?: string;
  end?: string;
  extras: string[];
}

function getActualPhaseSummary(
  tab: Tab,
  params: ProcessParameter[],
  batch: TimelineBatch,
  form?: Record<string, string>
): PhaseActualSummary | null {
  const raw = paramRaw(params, batch);
  const startKey = TAB_START_KEYS[tab];
  const endKey = TAB_END_KEYS[tab];

  const start =
    form?.[startKey]?.trim() ||
    (raw ? processParamTimeToInput(raw[startKey]) : "") ||
    "";
  const end =
    form?.[endKey]?.trim() ||
    (raw ? processParamTimeToInput(raw[endKey]) : "") ||
    "";

  if (!start && !end) return null;

  const extras: string[] = [];
  if (tab === "mixer") {
    const masa = form?.DoughMassKg?.trim() || raw?.DoughMassKg;
    if (masa != null && String(masa).trim() !== "") extras.push(`${masa} kg`);
    const temp = form?.DoughTempC?.trim() || raw?.DoughTempC;
    if (temp != null && String(temp).trim() !== "") extras.push(`${temp} °C`);
  }
  if (tab === "oven") {
    const temp = form?.BakingTempC?.trim() || raw?.BakingTempC;
    if (temp != null && String(temp).trim() !== "") extras.push(`${temp} °C`);
  }

  return {
    start: start || undefined,
    end: end || undefined,
    extras,
  };
}

function formatActualTimeRange(actual: PhaseActualSummary): string {
  if (actual.start && actual.end) return `${actual.start} – ${actual.end}`;
  if (actual.start) return `${actual.start} – …`;
  if (actual.end) return `… – ${actual.end}`;
  return "";
}

function resolveGlobalBatchStatus(
  batch: TimelineBatch,
  params: ProcessParameter[]
): TabPhaseStatus {
  const phases: Tab[] = ["mixer", "processor", "oven", "packaging"];
  const applicable = phases.filter((p) => batchNeedsPhase(batch, p));
  const statuses = applicable.map((p) => getTabPhaseStatus(p, params, batch));
  if (statuses.every((s) => s === "completed")) return "completed";
  if (statuses.some((s) => s !== "planned")) return "in_progress";
  return "planned";
}

function planned(batch: TimelineBatch, tab: Tab) {
  if (tab === "mixer")
    return {
      s: batch.MixingStart,
      e: batch.MixingEnd,
      m: batch.MixerName,
      dur: batch.MixingTimeMin,
    };
  if (tab === "processor")
    return {
      s: batch.ProcessingStart,
      e: batch.ProcessingEnd,
      m: batch.ProcessName,
      dur: batch.ProcessingTimeMin,
    };
  if (tab === "oven")
    return {
      s: batch.BakingStart,
      e: batch.BakingEnd,
      m: batch.OvenName,
      dur: batch.BakingTimeMin,
    };
  // Packaging has no scheduled slot on the batch; it starts after baking ends.
  return {
    s: batch.BakingEnd,
    e: undefined,
    m: undefined,
    dur: undefined,
  };
}

function scheduleDurationMin(start?: string, end?: string): number | null {
  if (!start || !end) return null;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  if (diff <= 0) return null;
  return Math.round(diff / 60000);
}

function timeInputToMinutes(hhmm?: string): number | null {
  if (!hhmm) return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

/** Drži odstupanje u rasponu ±12h (rješava prijelaz preko ponoći za male pomake). */
function normalizeDelta(min: number): number {
  let d = min;
  if (d > 720) d -= 1440;
  if (d < -720) d += 1440;
  return d;
}

function shiftTimeInput(hhmm: string, deltaMin: number): string {
  const base = timeInputToMinutes(hhmm);
  if (base == null) return "";
  let total = (base + deltaMin) % 1440;
  if (total < 0) total += 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
}

/** Odstupanje stvarnog od planiranog vremena faze (min). Pozitivno = kasni. */
function phaseActualVsPlannedDelay(
  phase: Tab,
  raw: Record<string, unknown> | undefined,
  batch: TimelineBatch
): number | null {
  if (!raw) return null;
  const pl = planned(batch, phase);

  const endActual = processParamTimeToInput(raw[TAB_END_KEYS[phase]]);
  const plannedEnd = toTimeInput(pl.e);
  if (endActual && plannedEnd) {
    const a = timeInputToMinutes(endActual);
    const b = timeInputToMinutes(plannedEnd);
    if (a != null && b != null) return normalizeDelta(a - b);
  }

  const startActual = processParamTimeToInput(raw[TAB_START_KEYS[phase]]);
  const plannedStart = toTimeInput(pl.s);
  if (startActual && plannedStart) {
    const a = timeInputToMinutes(startActual);
    const b = timeInputToMinutes(plannedStart);
    if (a != null && b != null) return normalizeDelta(a - b);
  }

  return null;
}

interface PhasePrediction {
  delayMin: number;
  start?: string;
  end?: string;
}

/**
 * Predviđa pomak faze na temelju stvarnih vremena prethodnih faza.
 * Prva faza (mikser) nema prethodnu fazu pa se preskače.
 */
/** Odstupanje na temelju stvarnog početka same faze (min). Pozitivno = kasni. */
function phaseOwnStartDelay(
  phase: Tab,
  raw: Record<string, unknown> | undefined,
  batch: TimelineBatch
): number | null {
  if (!raw) return null;
  const startActual = processParamTimeToInput(raw[TAB_START_KEYS[phase]]);
  const plannedStart = toTimeInput(planned(batch, phase).s);
  if (startActual && plannedStart) {
    const a = timeInputToMinutes(startActual);
    const b = timeInputToMinutes(plannedStart);
    if (a != null && b != null) return normalizeDelta(a - b);
  }
  return null;
}

function getPhasePrediction(
  tab: Tab,
  params: ProcessParameter[],
  batch: TimelineBatch
): PhasePrediction | null {
  // Mixer has no predecessor; packaging has no scheduled slot to predict against.
  if (tab === "mixer" || tab === "packaging") return null;
  const raw = paramRaw(params, batch);
  if (!raw) return null;

  // Ako je faza već započela, predikcija se temelji na njezinom stvarnom početku.
  let delay: number | null = phaseOwnStartDelay(tab, raw, batch);

  // Inače, naslijedi kašnjenje od prethodne faze.
  if (delay == null) {
    if (tab === "processor") {
      delay = phaseActualVsPlannedDelay("mixer", raw, batch);
    } else {
      if (batchNeedsPhase(batch, "processor")) {
        delay = phaseActualVsPlannedDelay("processor", raw, batch);
      }
      if (delay == null) delay = phaseActualVsPlannedDelay("mixer", raw, batch);
    }
  }
  if (delay == null) return null;

  const pl = planned(batch, tab);
  const ps = toTimeInput(pl.s);
  const pe = toTimeInput(pl.e);
  return {
    delayMin: delay,
    start: ps ? shiftTimeInput(ps, delay) : undefined,
    end: pe ? shiftTimeInput(pe, delay) : undefined,
  };
}

function formatDelayLabel(delayMin: number): string {
  if (delayMin > 0) return t("Kasni {min} min", { min: delayMin });
  if (delayMin < 0) return t("Rani {min} min", { min: Math.abs(delayMin) });
  return t("Na vrijeme");
}

/** Spaja datum proizvodnje (YYYY-MM-DD) i vrijeme (HH:MM) u Date. */
function parseDateTime(date: string, hhmm?: string): Date | null {
  if (!hhmm) return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const d = new Date(`${date}T${m[1].padStart(2, "0")}:${m[2]}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Proteklo vrijeme kao H:MM:SS (ili MM:SS ispod sat vremena). */
function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
}

const TabletWorkOrders = () => {
  savePathToLocalStorage();
  const [allBatches, setAllBatches] = useState<ProductionDayBatch[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(getDefaultMesDate());
  const [tab, setTab] = useState<Tab>("mixer");
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [params, setParams] = useState<ProcessParameter[]>([]);
  const [forms, setForms] = useState<Record<number, Record<string, string>>>({});
  const [now, setNow] = useState(() => Date.now());

  /** Procesna polja koja ovaj odjel unosi (processFields.ts). */
  const tabFields = useMemo(() => fieldsForTab(tab), [tab]);

  // Otkucaj svake sekunde za žive brojače aktivnih operacija.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Production-day view: everything physically produced on this day, incl.
      // semi-finished (starter, enzimi…) made today for other days' plans. This
      // attributes each batch to the day it is actually made.
      const [{ batches: dayBatches }, paramList] = await Promise.all([
        mesApi.getProductionDay(selectedDate),
        mesApi.getProcessParameters({ production_date: selectedDate }),
      ]);
      setAllBatches(dayBatches);
      setLoaded(true);
      setParams(paramList);
      setForms({});

      const needsStatusSync = dayBatches.filter((b) => {
        const target = resolveGlobalBatchStatus(b, paramList);
        return b.BatchStatus !== target;
      });
      if (needsStatusSync.length > 0) {
        await Promise.all(
          needsStatusSync.map((b) =>
            mesApi.updateBatchStatus(
              b.ID,
              resolveGlobalBatchStatus(b, paramList),
              "manual"
            )
          )
        );
        const { batches: refreshed } = await mesApi.getProductionDay(selectedDate);
        setAllBatches(refreshed);
      }
      // Recepture se više ne dohvaćaju ovdje: sirovine zamjesa (s dodijeljenim
      // lotom i recepturnom potrebom) dolaze iz BatchMaterialConfirm-a, po
      // otvorenoj kartici — dan s 30 zamjesa je prije radio jedan zahtjev za
      // recepturu po proizvodu i prije nego što je operater bilo što otvorio.
    } catch (e: any) {
      toast.error(t("Greška: {message}", { message: e.message }));
      setAllBatches([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const batches = useMemo(() => {
    const list = filterBatches(allBatches, tab);
    // Stabilan sort: završene faze idu na dno, redoslijed unutar grupa ostaje.
    return [...list].sort((a, b) => {
      const da = getTabPhaseStatus(tab, params, a) === "completed" ? 1 : 0;
      const db = getTabPhaseStatus(tab, params, b) === "completed" ? 1 : 0;
      return da - db;
    });
  }, [allBatches, tab, params]);

  const liRefs = useRef<Map<number, HTMLLIElement>>(new Map());
  const prevPositions = useRef<Map<number, number>>(new Map());

  // FLIP: lagana tranzicija kad se kartice preslože (npr. završena padne na dno).
  // Ovisi isključivo o `batches` (memoizirano) — inače bi se izvršavao nakon
  // svakog rendera, uključujući sekundne `now` otkucaje, pa bi se lista trzala
  // i "klizila" pri svakom skrolu.
  useLayoutEffect(() => {
    const next = new Map<number, number>();
    liRefs.current.forEach((el, id) => next.set(id, el.getBoundingClientRect().top));
    next.forEach((top, id) => {
      const prev = prevPositions.current.get(id);
      const el = liRefs.current.get(id);
      if (prev != null && el && prev !== top) {
        const delta = prev - top;
        el.style.transition = "none";
        el.style.transform = `translateY(${delta}px)`;
        requestAnimationFrame(() => {
          el.style.transition = "transform 0.35s ease";
          el.style.transform = "";
        });
      }
    });
    prevPositions.current = next;
  }, [batches]);

  const buildForm = (batch: TimelineBatch): Record<string, string> => {
    const raw = paramRaw(params, batch);
    const fields = fieldsForTab(tab);
    const prediction = getPhasePrediction(tab, params, batch);
    // Ako faza kasni, predloženo (default) vrijeme početka/kraja je predviđeno.
    const useDelayedDefault = prediction != null && prediction.delayMin > 0;

    // Spremljene vrijednosti se UVIJEK prepopune. Prije su se ne-vremenska
    // polja praznila dok faza nije završena, a spremanje piše null za prazno
    // polje — zbog toga je drugo "Spremi" na fazi u tijeku brisalo ono što je
    // operater upisao pri prvom.
    const f: Record<string, string> = buildProcessForm(raw, fields);

    fields.forEach((fld) => {
      if (fld.type !== "time") return;
      // Defaulti se UČITAVAJU pri otvaranju zadatka (samo u memorijsku formu).
      // Ništa se ne sprema u bazu dok korisnik ne klikne "Spremi"
      // (vidi persistForm/saveComplete; Start/Kraj samo upisuju u formu).
      if (f[fld.key]) return;
      const batchKey = TIME_DEFAULT_FROM_BATCH[fld.key];
      const plannedDefault = batchKey ? toTimeInput(batch[batchKey] as string) : "";
      const isStart = TAB_START_KEYS[tab] === fld.key;
      const isEnd = TAB_END_KEYS[tab] === fld.key;
      const delayedDefault = useDelayedDefault
        ? (isStart ? prediction?.start : isEnd ? prediction?.end : "") || ""
        : "";
      f[fld.key] = delayedDefault || plannedDefault;
    });

    if (tab === "mixer" && !f.DoughMassKg && batch.FinalMassKg) {
      f.DoughMassKg = String(batch.FinalMassKg);
    }

    return f;
  };

  const getForm = (batch: TimelineBatch) => forms[batch.ID] || buildForm(batch);

  const setField = (batchId: number, key: string, value: string, batch: TimelineBatch) => {
    setForms((prev) => ({
      ...prev,
      [batchId]: { ...(prev[batchId] || buildForm(batch)), [key]: value },
    }));
  };

  const openExpand = (batch: TimelineBatch) => {
    const next = expandedId === batch.ID ? null : batch.ID;
    setExpandedId(next);
    if (next) {
      setForms((prev) => ({ ...prev, [batch.ID]: buildForm(batch) }));
    }
  };

  /** Sprema parametre faze i usklađuje status batcha. Vraća nove parametre. */
  const persistForm = async (
    batch: TimelineBatch,
    form: Record<string, string>
  ): Promise<ProcessParameter[]> => {
    // Piše samo polja ove faze (+ njihova izračunata, npr. Scrap = oblik + boja),
    // pa spremanje na mikseru ne dira ono što je peć ili pakiranje upisalo.
    const payload: Record<string, unknown> = {
      MixCode: batch.MixCode,
      TimelineBatch: batch.ID,
      Product: batch.ProductID,
      ProductionDate: selectedDate,
      ...processPayloadFromForm(form, fieldsForTab(tab)),
    };

    const ex = findParam(params, batch);
    const saved = ex
      ? await mesApi.updateProcessParameter(ex.ID, payload)
      : await mesApi.createProcessParameter(payload);

    const nextParams = ex
      ? params.map((p) =>
          p.ID === ex.ID
            ? ({ ...p, ...(saved as unknown as Record<string, unknown>) } as ProcessParameter)
            : p
        )
      : [...params, saved];
    setParams(nextParams);

    const globalStatus = resolveGlobalBatchStatus(batch, nextParams);
    if (batch.BatchStatus !== globalStatus) {
      await mesApi.updateBatchStatus(batch.ID, globalStatus, "manual");
    }
    return nextParams;
  };

  const saveComplete = async (batch: TimelineBatch) => {
    const form = getForm(batch);
    setSavingId(batch.ID);
    try {
      await persistForm(batch, form);
      toast.success(t("Zadatak spremljen"));
      setExpandedId(null);
      load();
    } catch (e: any) {
      toast.error(t("Greška: {message}", { message: e.message }));
    } finally {
      setSavingId(null);
    }
  };

  /** Upiše trenutno vrijeme sata u polje početka/kraja — SAMO u formu. Trajno se
   *  sprema tek klikom na "Spremi" (persistForm/saveComplete). */
  const stampTime = (batch: TimelineBatch, which: "start" | "end") => {
    const key = which === "start" ? TAB_START_KEYS[tab] : TAB_END_KEYS[tab];
    setForms((prev) => ({
      ...prev,
      [batch.ID]: { ...(prev[batch.ID] || buildForm(batch)), [key]: nowTime() },
    }));
    if (expandedId !== batch.ID) setExpandedId(batch.ID);
  };

  const toggleFullscreen = () => {
    setFullscreen((prev) => {
      const next = !prev;
      if (next && document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
      } else if (!next && document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
      return next;
    });
  };

  const taskList = (
    <ul className="tablet-wo-list">
      {batches.map((batch) => {
        const p = planned(batch, tab);
        const open = expandedId === batch.ID;
        const form = getForm(batch);
        const phaseStatus = getTabPhaseStatus(tab, params, batch);
        const done = phaseStatus === "completed";
        const durMin = scheduleDurationMin(p.s, p.e) ?? p.dur ?? null;
        const actual = getActualPhaseSummary(tab, params, batch, form);
        const prediction = !done ? getPhasePrediction(tab, params, batch) : null;
        const running = phaseStatus === "in_progress";
        const startDate = running ? parseDateTime(selectedDate, actual?.start) : null;
        const runningMs = startDate ? now - startDate.getTime() : null;
        return (
          <li
            key={batch.ID}
            ref={(el) => {
              if (el) liRefs.current.set(batch.ID, el);
              else liRefs.current.delete(batch.ID);
            }}
            className={`tablet-wo-card ${done ? "done" : ""} ${open ? "open" : ""}`}
          >
            <button type="button" className="tablet-wo-card-head" onClick={() => openExpand(batch)}>
              <div className="tablet-wo-head-row">
                <div className="tablet-wo-head-titles">
                  <span className="tablet-wo-title">{batch.ProductName}</span>
                  <span className="tablet-wo-id">{batch.MixCode}</span>
                </div>
                <span className={`tablet-wo-status status-${phaseStatus}`}>
                  {t(STATUS_LABELS[phaseStatus])}
                </span>
              </div>
              {!open && (
                <div className="tablet-wo-schedule tablet-wo-schedule--plan">
                  <span className="tablet-wo-schedule-tag">{t("Plan")}</span>
                  <span className="tablet-wo-schedule-time">
                    {fmtTime(p.s)} – {fmtTime(p.e)}
                  </span>
                  {durMin != null && (
                    <span className="tablet-wo-schedule-dur">{durMin} min</span>
                  )}
                </div>
              )}
              {!open && actual && done && (
                <div className="tablet-wo-schedule tablet-wo-schedule--actual">
                  <span className="tablet-wo-schedule-tag">{t("Stvarno")}</span>
                  <span className="tablet-wo-schedule-time">{formatActualTimeRange(actual)}</span>
                  {actual.extras.map((x) => (
                    <span key={x} className="tablet-wo-schedule-extra">
                      {x}
                    </span>
                  ))}
                </div>
              )}
              {!open && prediction && (
                <div
                  className={`tablet-wo-schedule tablet-wo-schedule--predicted delay-${
                    prediction.delayMin > 0 ? "late" : prediction.delayMin < 0 ? "early" : "ontime"
                  }`}
                >
                  <span className="tablet-wo-schedule-tag">{formatDelayLabel(prediction.delayMin)}</span>
                  {(prediction.start || prediction.end) && (
                    <span className="tablet-wo-schedule-time">
                      {t("predviđeno {start} – {end}", { start: prediction.start || "…", end: prediction.end || "…" })}
                    </span>
                  )}
                </div>
              )}
              {!open && running && (
                <div className="tablet-wo-schedule tablet-wo-schedule--running">
                  <span className="tablet-wo-running-dot" aria-hidden />
                  <span className="tablet-wo-schedule-tag">{t("U tijeku")}</span>
                  <span className="tablet-wo-timer">
                    {runningMs != null ? formatElapsed(runningMs) : "—"}
                  </span>
                  {actual?.start && (
                    <span className="tablet-wo-schedule-time">{t("od {time}", { time: actual.start })}</span>
                  )}
                </div>
              )}
              <div className="tablet-wo-head-footer">
                <span className="tablet-wo-meta">
                  <span className="tablet-wo-meta-item">{batch.FinalMassKg} kg</span>
                  <span className="tablet-wo-meta-sep">·</span>
                  <span className="tablet-wo-meta-item">
                    {t(TAB_LABELS[tab])}: {p.m || "—"}
                  </span>
                </span>
                <span className="tablet-wo-chevron" aria-hidden>
                  {open ? "▲" : "▼"}
                </span>
              </div>
            </button>
            {open && (
              <div className="tablet-wo-body">
                {running && (
                  <div className="tablet-wo-timer-banner">
                    <span className="tablet-wo-running-dot" aria-hidden />
                    <span className="tablet-wo-timer-banner-label">Vrijeme rada</span>
                    <span className="tablet-wo-timer-banner-value">
                      {runningMs != null ? formatElapsed(runningMs) : "—"}
                    </span>
                  </div>
                )}
                {/* Sirovine koje idu u mikser: dodijeljeni lotovi, izmjena na
                    licu mjesta i potvrda utroška. Zamijenilo je statični ispis
                    recepta — brojke su iste, ali su sada uređive i potvrdive. */}
                {tab === "mixer" && <BatchMaterialConfirm batchId={batch.ID} onChanged={load} />}
                <div className="tablet-wo-fields">
                  {tabFields.some((f) => f.type === "time") && (
                    <p className="tablet-wo-time-hint">
                      {t("Vremena su predpopunjena planom; uredi po potrebi ili upiši trenutno vrijeme tipkama Početak/Kraj. Spremaju se tek klikom na „Spremi“. Odnose se na datum proizvodnje ({date}).", { date: new Date(selectedDate).toLocaleDateString(dateLocale()) })}
                    </p>
                  )}
                  {tabFields.map((fld) =>
                    fld.derived ? (
                      // Izračunato iz upisanih polja (npr. Δ pH, škart ukupno) —
                      // prikazuje se, sprema se, ne unosi se ručno.
                      <label key={fld.key} className="tablet-wo-field">
                        <span>{fld.label}</span>
                        <input
                          type="text"
                          readOnly
                          disabled
                          className="tablet-wo-input"
                          value={derivedDisplayValue(form, fld.key)}
                        />
                      </label>
                    ) : (
                      <label key={fld.key} className="tablet-wo-field">
                        <span>{fld.label}</span>
                        <input
                          type={fld.type === "textarea" ? "text" : fld.type}
                          step={fld.step}
                          className="tablet-wo-input"
                          value={form[fld.key] || ""}
                          onChange={(e) => setField(batch.ID, fld.key, e.target.value, batch)}
                        />
                      </label>
                    )
                  )}
                </div>
                <PhaseMaterialConfirm batchId={batch.ID} phaseCodes={TAB_PHASE_CODES[tab]} />
                <div className="tablet-wo-actions">
                  {tabFields.some(
                    (f) => f.type === "time" && f.key === TAB_START_KEYS[tab]
                  ) && (
                    <button
                      type="button"
                      className="tablet-wo-btn start"
                      disabled={savingId === batch.ID}
                      onClick={() => stampTime(batch, "start")}
                    >
                      ▶ {t("Početak = sad")}
                    </button>
                  )}
                  {tabFields.some(
                    (f) => f.type === "time" && f.key === TAB_END_KEYS[tab]
                  ) && (
                    <button
                      type="button"
                      className="tablet-wo-btn stop"
                      disabled={savingId === batch.ID}
                      onClick={() => stampTime(batch, "end")}
                    >
                      ■ {t("Kraj = sad")}
                    </button>
                  )}
                  <button
                    type="button"
                    className="tablet-wo-btn primary"
                    disabled={savingId === batch.ID}
                    onClick={() => saveComplete(batch)}
                  >
                    {savingId === batch.ID ? t("Spremanje…") : t("Spremi")}
                  </button>
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  const content = (
    <div className={`tablet-wo ${fullscreen ? "tablet-wo--fs" : ""}`}>
      {!fullscreen && (
        <div className="tablet-wo-date-bar">
          <MesDateSelector
            selectedDate={selectedDate}
            onDateChange={(d) => {
              saveMesDate(d);
              setSelectedDate(d);
            }}
            showTimelineIndicators
          />
        </div>
      )}
      <div className="tablet-wo-toolbar">
        {fullscreen && (
          <span className="tablet-wo-date">{new Date(selectedDate).toLocaleDateString(dateLocale())}</span>
        )}
        <div className="tablet-wo-tabs">
          {(Object.keys(TAB_LABELS) as Tab[]).map((tabId) => (
            <button
              key={tabId}
              type="button"
              className={tab === tabId ? "active" : ""}
              onClick={() => {
                setTab(tabId);
                setExpandedId(null);
                setForms({});
              }}
            >
              {t(TAB_LABELS[tabId])}
            </button>
          ))}
        </div>
        <button type="button" className="tablet-wo-fs-btn" onClick={toggleFullscreen}>
          {fullscreen ? t("Izađi") : t("Cijeli zaslon")}
        </button>
        <button type="button" className="tablet-wo-refresh" onClick={load}>
          ↻
        </button>
      </div>

      {loading && <p className="tablet-wo-msg">{t("Učitavanje…")}</p>}
      {!loading && allBatches.length === 0 && (
        <p className="tablet-wo-msg">
          {loaded
            ? t("Nema potvrđene proizvodnje za {date}.", { date: selectedDate })
            : t("Nema timelinea za {date}.", { date: selectedDate })}
        </p>
      )}
      {!loading && allBatches.length > 0 && batches.length === 0 && (
        <p className="tablet-wo-msg">{t("Nema zadataka za ovaj odjel.")}</p>
      )}
      {!loading && allBatches.length > 0 && batches.length > 0 && taskList}

      <ToastContainer position="top-center" autoClose={3000} theme="colored" />
    </div>
  );

  if (fullscreen) {
    return <div className="tablet-wo-shell">{content}</div>;
  }

  return (
    <div className="mes-page tablet-wo-page">
      <Header title={t("MES - Radni nalozi (tablet)")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">{content}</div>
      </div>
    </div>
  );
};

export default TabletWorkOrders;
